import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PageHeader } from '../components/AppLayout';
import { formatRelative } from '../constants/users';
import {
  Archive, ArrowLeft, ProjectIcon, Folder, MessageSquare,
  CheckSquare, ChevronRight, Repeat, Target, Search, X,
} from '../components/icons';

// ===========================================================================
// ArchivesPage — unified workspace archive (projects, tasks, objectives, groups)
// ===========================================================================
const KINDS = [
  { id: 'all',        label: 'Tout',       color: 'var(--p)',    icon: Archive },
  { id: 'projects',   label: 'Projets',    color: '#A78BFA',     icon: Folder },
  { id: 'tasks',      label: 'Tâches',     color: '#2EA3F2',     icon: CheckSquare },
  { id: 'objectives', label: 'Objectifs',  color: '#F5A623',     icon: Target },
  { id: 'convs',      label: 'Groupes',    color: '#34D399',     icon: MessageSquare },
];

export default function ArchivesPage() {
  const { isFounder } = useAuth();
  const nav = useNavigate();
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ projects: [], tasks: [], objectives: [], convs: [] });
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [p, t, o, c] = await Promise.all([
        api.get('/projects?archived=true'),
        api.get('/tasks?archived=true'),
        api.get('/objectives?archived=true'),
        api.get('/conversations?archived=true'),
      ]);
      setData({
        projects: p.data,
        tasks: t.data,
        objectives: o.data,
        convs: c.data,
      });
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const restore = async (k, id) => {
    try {
      if (k === 'project')   await api.patch(`/projects/${id}`,   { archived: false });
      if (k === 'task')      await api.patch(`/tasks/${id}`,      { archived: false });
      if (k === 'objective') await api.patch(`/objectives/${id}`, { archived: false });
      if (k === 'conv')      await api.patch(`/conversations/${id}`, { archived: false });
      toast.success('Restauré');
      reload();
    } catch { toast.error('Erreur'); }
  };

  const counts = {
    all: data.projects.length + data.tasks.length + data.objectives.length + data.convs.length,
    projects: data.projects.length,
    tasks: data.tasks.length,
    objectives: data.objectives.length,
    convs: data.convs.length,
  };

  // Build a unified item list
  const matches = (txt) => !search || (txt || '').toLowerCase().includes(search.toLowerCase());

  const allItems = useMemo(() => {
    const items = [];
    if (kind === 'all' || kind === 'projects') data.projects.filter((p) => matches(p.name)).forEach((p) => items.push({
      kind: 'project', id: p.id, color: p.color || '#A78BFA',
      icon: <ProjectIcon name={p.icon} color={p.color} size={16} />,
      title: p.name, sub: p.client || '—', date: p.archived_at || p.updated_at,
    }));
    if (kind === 'all' || kind === 'tasks') data.tasks.filter((t) => matches(t.title)).forEach((t) => items.push({
      kind: 'task', id: t.id, color: '#2EA3F2',
      icon: <CheckSquare size={14} color="#2EA3F2" />,
      title: t.title, sub: t.description || (t.tags || []).join(' · ') || '—', date: t.updated_at,
    }));
    if (kind === 'all' || kind === 'objectives') data.objectives.filter((o) => matches(o.text)).forEach((o) => items.push({
      kind: 'objective', id: o.id, color: '#F5A623',
      icon: <Target size={14} color="#F5A623" />,
      title: o.text, sub: `Semaine du ${o.week_start || '—'}`, date: o.updated_at,
    }));
    if (kind === 'all' || kind === 'convs') data.convs.filter((c) => matches(c.name)).forEach((c) => items.push({
      kind: 'conv', id: c.id, color: '#34D399',
      icon: <MessageSquare size={14} color="#34D399" />,
      title: c.name || 'Groupe', sub: `${(c.members || []).length} membres`, date: c.archived_at || c.updated_at,
      onOpen: () => { localStorage.setItem('asa_open_conv', c.id); nav('/messages'); },
    }));
    // Sort by date desc
    items.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return items;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, kind, search]);

  return (
    <div className="page-fade">
      <PageHeader
        title="Archives"
        subtitle="Projets, tâches, objectifs et groupes terminés"
        count={counts.all || null}
        right={
          <button onClick={() => nav(-1)} className="asa-btn-ghost asa-btn">
            <ArrowLeft size={14} /> Retour
          </button>
        }
      />

      <div style={{ padding: '0 22px 100px' }}>
        {/* Search */}
        <div className="asa-field" style={{ marginBottom: 14, maxWidth: 460 }}>
          <Search size={13} color="var(--t3)" />
          <input
            data-testid="archives-search"
            placeholder="Rechercher dans les archives…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%' }}
          />
          {search && <button onClick={() => setSearch('')}><X size={13} color="var(--t3)" /></button>}
        </div>

        {/* Multi-kind filter pills */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
          {KINDS.map((k) => {
            const Icon = k.icon;
            const active = kind === k.id;
            return (
              <button
                key={k.id}
                data-testid={`archives-kind-${k.id}`}
                onClick={() => setKind(k.id)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 13px', borderRadius: 999,
                  background: active ? `${k.color}1F` : 'var(--sf)',
                  border: `1.5px solid ${active ? k.color : 'var(--bd)'}`,
                  color: active ? k.color : 'var(--t3)',
                  fontFamily: 'Sora', fontWeight: 600, fontSize: '0.76rem',
                  cursor: 'pointer',
                }}
              >
                <Icon size={12} /> {k.label}
                <span style={{ opacity: 0.7, fontSize: '0.66rem' }}>{counts[k.id]}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="shimmer" style={{ height: 240, borderRadius: 14 }} />
        ) : allItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--t3)' }}>
            <Archive size={36} color="var(--bd)" strokeWidth={1.2} />
            <div className="t-display-sm" style={{ marginTop: 12 }}>Rien d'archivé ici</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {allItems.map((it) => (
              <ArchiveRow
                key={`${it.kind}-${it.id}`}
                item={it}
                onRestore={isFounder || it.kind === 'task' || it.kind === 'objective' ? () => restore(it.kind, it.id) : null}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ArchiveRow({ item, onRestore }) {
  return (
    <div
      data-testid={`archive-${item.kind}-${item.id}`}
      className="asa-card"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 9,
        background: `${item.color}1A`, border: `1.5px solid ${item.color}44`,
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>{item.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.86rem', color: 'var(--t1)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            flex: 1, minWidth: 0,
          }}>{item.title}</span>
          <span style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.58rem',
            padding: '2px 7px', borderRadius: 999,
            background: `${item.color}1A`, color: item.color,
            textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0,
          }}>{KIND_LABEL[item.kind]}</span>
        </div>
        <div style={{
          fontFamily: 'Lexend', fontSize: '0.7rem', color: 'var(--t3)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1,
        }}>{item.sub}</div>
      </div>
      <span style={{ fontFamily: 'Lexend', fontSize: '0.64rem', color: 'var(--t3)' }}>
        {item.date ? formatRelative(item.date) : ''}
      </span>
      {onRestore && (
        <button
          onClick={onRestore}
          className="asa-btn-ghost asa-btn"
          style={{ padding: '5px 11px', fontSize: '0.72rem' }}
        >
          <Repeat size={11} /> Restaurer
        </button>
      )}
      {item.onOpen && (
        <button
          onClick={item.onOpen}
          className="asa-btn-ghost asa-btn"
          style={{ padding: '5px 11px', fontSize: '0.72rem' }}
        >
          Ouvrir <ChevronRight size={11} />
        </button>
      )}
    </div>
  );
}

const KIND_LABEL = { project: 'Projet', task: 'Tâche', objective: 'Objectif', conv: 'Groupe' };
